import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useI18n } from "../../context/I18nContext";

export const Projects = () => {
  const { t } = useI18n();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const STATUS_LABEL = {
    nouveau: t("projects.status_nouveau"),
    en_cours: t("projects.status_en_cours"),
    livre: t("projects.status_livre"),
    annule: t("projects.status_annule"),
  };
  const STATUS_STYLE = {
    nouveau: "border-white/20 text-zinc-300",
    en_cours: "border-emerald-500/40 text-emerald-300",
    livre: "border-zinc-300/40 text-zinc-200",
    annule: "border-rose-500/40 text-rose-300",
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!title || !description) return;
    setCreating(true);
    try {
      await api.post("/projects", { title, description });
      toast.success(t("projects.created"));
      setTitle("");
      setDescription("");
      setOpen(false);
      load();
    } catch {
      toast.error(t("projects.create_failed"));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div data-testid="dashboard-projects">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-zinc-400">{t("projects.intro")}</p>
        <button
          type="button"
          data-testid="projects-new-btn"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-2 bg-white text-zinc-950 hover:bg-zinc-200 px-4 py-2.5 text-sm"
        >
          <Plus size={14} /> {t("projects.new")}
        </button>
      </div>

      {open && (
        <form onSubmit={create} data-testid="projects-form" className="bg-zinc-900/30 border border-white/10 p-6 mb-8 space-y-4">
          <Field label={t("projects.title")}>
            <input
              required
              data-testid="projects-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-zinc-950 border border-white/10 px-4 py-3 text-sm focus:border-white focus:outline-none focus:ring-1 focus:ring-white"
            />
          </Field>
          <Field label={t("projects.description")}>
            <textarea
              required
              rows={4}
              data-testid="projects-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-zinc-950 border border-white/10 px-4 py-3 text-sm focus:border-white focus:outline-none focus:ring-1 focus:ring-white resize-y"
            />
          </Field>
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={creating}
              data-testid="projects-submit"
              className="bg-white text-zinc-950 hover:bg-zinc-200 px-5 py-2.5 text-sm disabled:opacity-50"
            >
              {creating ? t("projects.creating") : t("projects.create")}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="border border-white/20 hover:border-white/60 px-5 py-2.5 text-sm"
            >
              {t("common.cancel")}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-sm text-zinc-500">{t("common.loading")}</p>
      ) : projects.length === 0 ? (
        <div className="border border-white/10 border-dashed p-12 text-center">
          <p className="text-zinc-400 text-sm">{t("projects.empty_t")}</p>
          <p className="text-zinc-600 text-xs mt-1">{t("projects.empty_d")}</p>
        </div>
      ) : (
        <div className="border border-white/10 divide-y divide-white/10">
          {projects.map((p) => (
            <div key={p.project_id} className="grid grid-cols-1 sm:grid-cols-12 gap-4 p-6 bg-zinc-950">
              <div className="sm:col-span-7">
                <p className="font-display text-xl tracking-tight">{p.title}</p>
                <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{p.description}</p>
              </div>
              <div className="sm:col-span-3 flex sm:justify-center sm:items-center">
                <span className={`text-xs uppercase tracking-[0.2em] border px-3 py-1 ${STATUS_STYLE[p.status] || ""}`}>
                  {STATUS_LABEL[p.status] || p.status}
                </span>
              </div>
              <div className="sm:col-span-2 flex sm:justify-end sm:items-center">
                <p className="font-mono text-xs text-zinc-500">{new Date(p.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 mb-2">{label}</span>
    {children}
  </label>
);
